import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { EnterpriseAutocompletedProfilePatchDto, EnterpriseAutocompletedField, EnterpriseAutocompletedFlags } from 'src/api/enterprise-profile/types';
import { Job, useJobQueue } from 'src/hooks/use-job-queue';
import container from 'src/config/inversify-config';
import { EnterpriseProfileFormApiService } from 'src/api/enterprise-profile/enterprise-profile-api';
import { CvProcessingStatus } from 'src/api/person-profile/types';
import { extractEnterpriseAutocompletedFields } from 'src/common/enterprise-linkedin-profile';
import { useLocalStorageState } from 'ahooks';
import { j2lStorageKeys } from 'src/common/j2l-cache';

type SetAutocompletedFields = (
  value: EnterpriseAutocompletedField[] | ((prev: EnterpriseAutocompletedField[] | undefined) => EnterpriseAutocompletedField[])
) => void;

type SetAutocompletedFlags = (value: EnterpriseAutocompletedFlags | ((prev: EnterpriseAutocompletedFlags) => EnterpriseAutocompletedFlags)) => void;

type PreviousFieldsMap = Record<string, EnterpriseAutocompletedField[] | undefined>;

interface EnterpriseLinkedinAnalysisContextType {
  autocompletedFields?: EnterpriseAutocompletedField[];
  setAutocompletedFields: SetAutocompletedFields;
  previousFieldsMap: PreviousFieldsMap;
  setPreviousFieldsForKey: (key: string, fields: EnterpriseAutocompletedField[] | undefined) => void;
  reviewedFlags: EnterpriseAutocompletedFlags;
  setReviewedFlags: SetAutocompletedFlags;
  enqueueLinkedInAutofill: (enterpriseProfileId: string, linkedInUrl: string) => Promise<boolean>;
  enqueueLinkedInJobPostAutofill: (enterpriseProfileId: string, linkedInJobPostUrl: string) => Promise<boolean>;
  cvProcessingStatus?: CvProcessingStatus;
  lastAutofillTimestamp?: number;
}

const EnterpriseLinkedinAnalysisContext = createContext<EnterpriseLinkedinAnalysisContextType | undefined>(undefined);

export const EnterpriseLinkedinAnalysisProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = auth.user?.profile?.sub ?? 'temp';

  const [cvProcessingStatus, setCvProcessingStatus] = useState<CvProcessingStatus | undefined>();
  const [lastAutofillTimestamp, setLastAutofillTimestamp] = useState<number | undefined>();

  const [autocompletedFields, setAutocompletedFields] = useLocalStorageState<EnterpriseAutocompletedField[]>(
    j2lStorageKeys.enterpriseAutocompletedFields(userId),
    {
      defaultValue: []
    }
  );
  const [previousFieldsMap, setPreviousFieldsMap] = useState<PreviousFieldsMap>({});

  const setPreviousFieldsForKey = (key: string, fields: EnterpriseAutocompletedField[] | undefined) => {
    setPreviousFieldsMap((prev) => ({
      ...prev,
      [key]: fields
    }));
  };

  const [reviewedFlags, setReviewedFlags] = React.useState<EnterpriseAutocompletedFlags>({});

  const onLinkedinCompanyResult = (result: EnterpriseAutocompletedProfilePatchDto) => {
    const fields = extractEnterpriseAutocompletedFields(result);

    if (fields.length === 0) {
      message.info(t('No se detectó información nueva en LinkedIn para completar el perfil.'), 5);
      setCvProcessingStatus(CvProcessingStatus.NoRelevantData);
    } else {
      message.success(t('Hemos autocompletado algunos campos del perfil de la empresa.'), 5);
      setCvProcessingStatus(CvProcessingStatus.Success);
    }

    setAutocompletedFields(fields);
    setLastAutofillTimestamp(Date.now());
  };

  const onLinkedinCompanyError = () => {
    message.error(t('Error al procesar el perfil de LinkedIn. Inténtalo de nuevo más tarde.'), 5);
    setCvProcessingStatus(CvProcessingStatus.Failed);
  };

  const { addJob: addCompanyJob, isProcessing: isProcessingCompany } = useJobQueue<EnterpriseAutocompletedProfilePatchDto>(
    j2lStorageKeys.enterpriseCompanyAnalysisJobs(userId),
    onLinkedinCompanyResult,
    onLinkedinCompanyError
  );

  const triggerCompanyAnalysis = useCallback(
    (jobId: string) => {
      const job: Job = {
        jobId,
        timestamp: Date.now(),
        maxAttempts: 24,
        interval: 5000,
        attemptsMade: 0
      };
      addCompanyJob(job);
    },
    [addCompanyJob]
  );

  const api = container.get<EnterpriseProfileFormApiService>(EnterpriseProfileFormApiService);

  const enqueueLinkedInAutofill = useCallback(
    async (enterpriseProfileId: string, linkedInUrl: string): Promise<boolean> => {
      try {
        const response = await api.autofillFromLinkedin(enterpriseProfileId, linkedInUrl);

        if (!response.isSuccess || !response.item?.jobId) {
          message.error(t(response.error ?? 'No hemos podido procesar el perfil de LinkedIn.'), 5);
          return false;
        }

        message.success(t('Estamos analizando la página de LinkedIn para autocompletar el perfil de la empresa.'), 5);

        triggerCompanyAnalysis(response.item.jobId);
        return true;
      } catch (error) {
        console.error('LinkedIn autofill error:', error);
        message.error(t('No hemos podido procesar el perfil de LinkedIn.'), 5);
        return false;
      }
    },
    [api, t, triggerCompanyAnalysis]
  );

  const onLinkedinJobPostResult = (result: EnterpriseAutocompletedProfilePatchDto) => {
    const fields = extractEnterpriseAutocompletedFields(result);

    if (fields.length === 0) {
      message.info(t('No se detectó información nueva en LinkedIn para completar el perfil.'), 5);
      setCvProcessingStatus(CvProcessingStatus.NoRelevantData);
    } else {
      message.success(t('Hemos autocompletado algunos campos de la oferta de LinkedIn.'), 5);
      setCvProcessingStatus(CvProcessingStatus.Success);
    }

    setAutocompletedFields(fields);
    setLastAutofillTimestamp(Date.now());
  };

  const onLinkedinJobPostError = () => {
    message.error(t('Error al procesar la oferta de LinkedIn. Inténtalo de nuevo más tarde.'), 5);
    setCvProcessingStatus(CvProcessingStatus.Failed);
  };

  const { addJob: addJobPostJob, isProcessing: isProcessingJobPost } = useJobQueue<EnterpriseAutocompletedProfilePatchDto>(
    j2lStorageKeys.enterpriseJobPostAnalysisJobs(userId),
    onLinkedinJobPostResult,
    onLinkedinJobPostError
  );

  const triggerJobPostAnalysis = useCallback(
    (jobId: string) => {
      addJobPostJob({
        jobId,
        timestamp: Date.now(),
        maxAttempts: 24,
        interval: 5000,
        attemptsMade: 0
      });
    },
    [addJobPostJob]
  );

  const enqueueLinkedInJobPostAutofill = useCallback(
    async (enterpriseProfileId: string, linkedInJobPostUrl: string): Promise<boolean> => {
      try {
        const response = await api.autofillFromLinkedinJobPost(enterpriseProfileId, linkedInJobPostUrl);

        if (!response.isSuccess || !response.item?.jobId) {
          message.error(t(response.error ?? 'No hemos podido procesar la oferta de LinkedIn.'), 5);
          return false;
        }

        message.success(t('Estamos analizando la oferta de LinkedIn para ayudarte a completar el perfil de la empresa.'), 5);

        triggerJobPostAnalysis(response.item.jobId);
        return true;
      } catch (error) {
        console.error('LinkedIn autofill error:', error);
        message.error(t('No hemos podido procesar la oferta de LinkedIn.'), 5);
        return false;
      }
    },
    [api, t, triggerJobPostAnalysis]
  );

  useEffect(() => {
    if (isProcessingCompany || isProcessingJobPost) setCvProcessingStatus(CvProcessingStatus.InProgress);
  }, [isProcessingCompany, isProcessingJobPost]);

  return (
    <EnterpriseLinkedinAnalysisContext.Provider
      value={{
        autocompletedFields,
        setAutocompletedFields,
        previousFieldsMap,
        setPreviousFieldsForKey,
        reviewedFlags,
        setReviewedFlags,
        enqueueLinkedInAutofill,
        enqueueLinkedInJobPostAutofill,
        cvProcessingStatus,
        lastAutofillTimestamp
      }}
    >
      {children}
    </EnterpriseLinkedinAnalysisContext.Provider>
  );
};

export const useEnterpriseLinkedinAnalysis = (): EnterpriseLinkedinAnalysisContextType => {
  const ctx = useContext(EnterpriseLinkedinAnalysisContext);
  if (!ctx) throw new Error('useEnterpriseAnalysis must be used inside EnterpriseAnalysisProvider');
  return ctx;
};
